import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { rupee, waLink, productWaText } from "@/lib/whatsapp";
import { setSeo, resetSeo } from "@/lib/seo";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Heart, ShoppingBag, MessageCircle, Share2, Star, Truck, ShieldCheck, Leaf, Minus, Plus, Loader2, ChevronRight } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addToCart, toggleWishlist, inWishlist, settings, cart } = useApp();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null);
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setActive(0); }).catch(() => nav("/shop"));
    api.get(`/products/${slug}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug, nav]);

  useEffect(() => { if (p) api.get(`/products/${p.id}/reviews`).then((r) => setReviews(r.data)).catch(() => {}); }, [p]);

  useEffect(() => {
    if (p) setSeo({
      title: p.seo_title || p.name, description: p.seo_description || p.short_description, image: p.images?.[0],
      jsonLd: { "@context": "https://schema.org", "@type": "Product", name: p.name, image: p.images,
        description: p.short_description, sku: p.sku, brand: { "@type": "Brand", name: p.brand },
        aggregateRating: p.review_count ? { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.review_count } : undefined,
        offers: { "@type": "Offer", priceCurrency: "INR", price: p.price, availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" } },
    });
    return () => resetSeo();
  }, [p]);

  if (!p) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;

  const wished = inWishlist(p.id);
  const out = p.stock <= 0;
  const share = () => {
    const url = `${window.location.origin}/product/${p.slug}`;
    if (navigator.share) navigator.share({ title: p.name, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Link copied!"); }
  };
  const buyNow = () => { addToCart(p, qty); nav("/checkout"); };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6 flex-wrap">
        <Link to="/" className="hover:text-forest">Home</Link><ChevronRight className="w-3.5 h-3.5" />
        <Link to="/shop" className="hover:text-forest">Shop</Link><ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/shop?category=${p.category_id}`} className="hover:text-forest">{p.category_name}</Link><ChevronRight className="w-3.5 h-3.5" />
        <span className="text-ink">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
        <div>
          <div className="aspect-square rounded-[28px] overflow-hidden bg-sand soft-shadow">
            <img src={p.images?.[active]} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-20 h-20 rounded-2xl overflow-hidden border-2 ${active === i ? "border-terracotta" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm uppercase tracking-wide text-olive font-500">{p.brand}</span>
            <span className="inline-flex items-center gap-1 text-sm text-brown"><Star className="w-4 h-4 fill-mustard text-mustard" />{p.rating || "New"} ({p.review_count})</span>
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">{p.name}</h1>
          <p className="text-brown/80 mt-3 leading-relaxed">{p.short_description}</p>

          <div className="flex items-end gap-3 mt-5">
            <span className="text-3xl font-600 text-forest">{rupee(p.price)}</span>
            {p.mrp > p.price && <span className="text-lg text-muted-foreground line-through">{rupee(p.mrp)}</span>}
            {p.discount > 0 && <span className="bg-terracotta text-white text-sm font-600 px-2.5 py-1 rounded-full mb-1">{p.discount}% OFF</span>}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            <span className="text-brown">Pack: <b>{p.pack_size}</b></span>
            <span className="text-brown">SKU: <b>{p.sku}</b></span>
            <span className={out ? "text-terracotta font-600" : "text-earth font-600"}>{out ? "Out of Stock" : `In Stock (${p.stock})`}</span>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {p.tags?.map((t) => <span key={t} className="text-xs bg-sage/20 text-forest px-3 py-1 rounded-full">{t}</span>)}
          </div>

          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-sage/50 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-11 flex items-center justify-center text-forest" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-500" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-10 h-11 flex items-center justify-center text-forest" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => toggleWishlist(p)} data-testid="pdp-wishlist-btn" className="w-11 h-11 rounded-full border border-sage/50 flex items-center justify-center hover:bg-sand transition-colors">
              <Heart className={`w-5 h-5 ${wished ? "fill-terracotta text-terracotta" : "text-forest"}`} />
            </button>
            <button onClick={share} data-testid="pdp-share-btn" className="w-11 h-11 rounded-full border border-sage/50 flex items-center justify-center hover:bg-sand transition-colors">
              <Share2 className="w-5 h-5 text-forest" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <Button onClick={() => addToCart(p, qty)} disabled={out} data-testid="pdp-add-cart-btn" className="rounded-full h-12 bg-forest hover:bg-earth text-cream"><ShoppingBag className="w-4 h-4 mr-1" /> Add to Cart</Button>
            <Button onClick={buyNow} disabled={out} data-testid="pdp-buy-now-btn" variant="outline" className="rounded-full h-12 border-terracotta text-terracotta hover:bg-terracotta hover:text-white">Buy Now</Button>
          </div>
          <a href={waLink(settings.whatsapp_number, productWaText(p))} target="_blank" rel="noreferrer" data-testid="pdp-wa-btn"
            className="mt-3 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eb85a] text-white rounded-full h-12 font-500 transition-colors">
            <MessageCircle className="w-5 h-5" /> Ask on WhatsApp
          </a>

          <div className="grid grid-cols-3 gap-3 mt-6 text-center">
            {[[Truck, "Fast Delivery"], [ShieldCheck, "Certified Organic"], [Leaf, "100% Natural"]].map(([Ic, t], i) => (
              <div key={i} className="bg-sand rounded-2xl py-3"><Ic className="w-5 h-5 text-olive mx-auto mb-1" /><span className="text-xs text-brown">{t}</span></div>
            ))}
          </div>
        </div>
      </div>

      <Tabs defaultValue="desc" className="mt-14">
        <TabsList className="bg-sand rounded-full h-auto p-1 flex-wrap">
          {[["desc", "Description"], ["benefits", "Benefits"], ["nutrition", "Nutrition & Ingredients"], ["reviews", `Reviews (${reviews.length})`]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream px-5 py-2">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="desc" className="mt-6 max-w-3xl">
          <p className="text-brown/80 leading-relaxed">{p.description}</p>
          <dl className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
            {[["Origin", p.origin], ["Manufacturer", p.manufacturer], ["Storage", p.storage], ["Usage", p.usage], ["Certifications", p.certifications?.join(", ")]].map(([k, v]) => v && (
              <div key={k} className="bg-white rounded-xl p-4 soft-shadow"><dt className="text-olive font-500">{k}</dt><dd className="text-ink mt-0.5">{v}</dd></div>
            ))}
          </dl>
        </TabsContent>
        <TabsContent value="benefits" className="mt-6 max-w-3xl">
          <ul className="space-y-2">{p.benefits?.map((b, i) => <li key={i} className="flex items-start gap-2 text-brown"><Leaf className="w-4 h-4 text-olive mt-1 shrink-0" /> {b}</li>)}</ul>
        </TabsContent>
        <TabsContent value="nutrition" className="mt-6 max-w-3xl space-y-4">
          <div className="bg-white rounded-xl p-4 soft-shadow"><h4 className="font-500 text-forest mb-1">Ingredients</h4><p className="text-sm text-brown/80">{p.ingredients}</p></div>
          <div className="bg-white rounded-xl p-4 soft-shadow"><h4 className="font-500 text-forest mb-1">Nutritional Information</h4><p className="text-sm text-brown/80">{p.nutrition}</p></div>
        </TabsContent>
        <TabsContent value="reviews" className="mt-6 max-w-3xl">
          <ReviewSection productId={p.id} reviews={reviews} setReviews={setReviews} />
        </TabsContent>
      </Tabs>

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-heading text-3xl text-forest tracking-tight mb-6">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">{related.slice(0, 4).map((r) => <ProductCard key={r.id} product={r} />)}</div>
        </div>
      )}
    </div>
  );
}

function ReviewSection({ productId, reviews, setReviews }) {
  const { user } = useApp();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = () => {
    if (!user) return toast.error("Please log in to leave a review");
    api.post(`/products/${productId}/reviews`, { rating, comment }).then((r) => { setReviews([r.data, ...reviews]); setComment(""); toast.success("Review added"); });
  };
  return (
    <div>
      {user && (
        <div className="bg-white rounded-2xl p-5 soft-shadow mb-6">
          <div className="flex gap-1 mb-3">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}><Star className={`w-6 h-6 ${n <= rating ? "fill-mustard text-mustard" : "text-sage"}`} /></button>)}</div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience..." data-testid="review-input"
            className="w-full border border-sage/40 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-sage/40" rows={3} />
          <Button onClick={submit} className="mt-3 rounded-full bg-forest hover:bg-earth text-cream" data-testid="review-submit-btn">Submit Review</Button>
        </div>
      )}
      {reviews.length === 0 ? <p className="text-muted-foreground">No reviews yet. Be the first!</p> : (
        <div className="space-y-4">{reviews.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl p-4 soft-shadow">
            <div className="flex items-center justify-between"><span className="font-500 text-ink">{r.user_name}</span><span className="flex gap-0.5">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-mustard text-mustard" />)}</span></div>
            <p className="text-sm text-brown/80 mt-1">{r.comment}</p>
          </div>
        ))}</div>
      )}
    </div>
  );
}
